import * as React from 'react';
import { ChangeEvent, FC, FormEvent, memo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

import { TwoFactorAuthentication } from '@/legacy/features/user/twoFactorAuthentication/twoFactorAuthentication';
import TitleMessageDialog from '@/legacy/modals/react/TitleMessageDialog';

import { State } from './TwoFactorSetupDialog.reducer';
import TwoFactorStepper from './TwoFactorStepper';

interface Props {
  twoFactorAuthenticationDisabler: TwoFactorAuthentication;
  twoFactorAuthenticationDisablers: Array<TwoFactorAuthentication>;
  state: State;
  isOpen: boolean;
  onSubmit: () => void;
  onClose: () => void;
  onCodeChange: { (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void };
  onNextStep: { (): void };
  onPhoneNumberChange: { (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void };
  onPreviousStep: { (): void };
  onSmsCountryChange: { (e: ChangeEvent<HTMLInputElement>): void };
  onVerificationChange: { (e: ChangeEvent<HTMLInputElement>): void };
  onUnableToScan: { (): void };
  onTwoFactorAuthenticationDisablerChange: (verification: TwoFactorAuthentication) => void;
  onResendCode: () => void;
  onCallMe: () => void;
}

const TwoFactorSetupDialogContents: FC<Props> = (props: Props): React.JSX.Element => {
  const {
    twoFactorAuthenticationDisabler,
    twoFactorAuthenticationDisablers,
    state,
    isOpen,
    onSubmit,
    onClose,
    onCodeChange,
    onNextStep,
    onPhoneNumberChange,
    onPreviousStep,
    onSmsCountryChange,
    onVerificationChange,
    onUnableToScan,
    onTwoFactorAuthenticationDisablerChange,
    onResendCode,
    onCallMe,
  } = props;

  const handeClose = () => {
    onClose();
  };

  const getDialog = () => {
    const { dialog } = state;
    if (!dialog) {
      return null;
    }

    return (
      <TitleMessageDialog
        title={dialog.title}
        message={dialog.message}
        onClose={() => {
          handeClose();
        }}
      />
    );
  };

  const getDialogTitle = (): string => {
    if (state.isEnabled) {
      return 'Disable';
    }

    return 'Enable';
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit();

    return false;
  };

  // @todo These need to be updated once we remove React.ChangeEvent
  const handleCodeChange = onCodeChange;
  const handleNextStep = onNextStep;
  const handlePhoneNumberChange = onPhoneNumberChange;
  const handlePreviousStep = onPreviousStep;
  const handleSmsCountryChange = onSmsCountryChange;
  const handleVerificationChange = onVerificationChange;
  const handleUnableToScan = onUnableToScan;
  const handleResendCode = onResendCode;
  const handleCallMe = onCallMe;

  return (
    <>
      {getDialog()}
      <Dialog
        open={isOpen}
        aria-labelledby="twofactor-dialog-title"
        aria-describedby="twofactor-dialog-description"
      >
        <DialogTitle id="twofactor-dialog-title">
          <FormattedMessage
            defaultMessage="{dialogTitle} Two-Step Authentication"
            values={{ dialogTitle: getDialogTitle() }}
          />
        </DialogTitle>

        <DialogContent>
          <DialogContentText id="twofactor-dialog-description">
            <form onSubmit={handleSubmit}>
              <TwoFactorStepper
                twoFactorAuthenticationDisabler={twoFactorAuthenticationDisabler}
                twoFactorAuthenticationDisablers={twoFactorAuthenticationDisablers}
                state={state}
                onCodeChange={handleCodeChange}
                onTwoFactorAuthenticationDisablerChange={onTwoFactorAuthenticationDisablerChange}
                onNextStep={handleNextStep}
                onPhoneNumberChange={handlePhoneNumberChange}
                onPreviousStep={handlePreviousStep}
                onSmsCountryChange={handleSmsCountryChange}
                onVerificationChange={handleVerificationChange}
                onUnableToScan={handleUnableToScan}
                onResendCode={handleResendCode}
                onCallMe={handleCallMe}
              />
            </form>
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <a
            target="_blank"
            href="https://www.plooto.com/support/s/article/enable-2-factor-authentication-2fa-for-your-users"
            rel="noopener noreferrer"
          >
            <Button variant="text">
              <FormattedMessage defaultMessage="Help" />
            </Button>
          </a>

          <div style={{ flexGrow: 1 }} />

          <Button variant="text" onClick={() => handeClose()} color="primary">
            <FormattedMessage defaultMessage="Close" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default memo(TwoFactorSetupDialogContents);
